import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import isDeepEqual from "fast-deep-equal";
import { computed, type MaybeRefOrGetter, toValue } from "vue";

import { debouncedRef } from "@/lib/composables/debounced-ref";
import { useStablePending } from "@/lib/composables/stable-pending";
import type { Newsletter } from "@/types/newsletter";

import { useNewsletterId, useXsrfHeaders } from "../utils";
import { fetchSubscribeTemplate } from "./helpers";
import type { PreviewSource, PreviewSourceFactory } from "./types";

interface SubscribePagePreviewInput {
  newsletter: Partial<Newsletter>;
}

export const useSubscribePagePreview = (
  input: MaybeRefOrGetter<SubscribePagePreviewInput>,
  options: {
    enabled?: MaybeRefOrGetter<boolean>;
  } = {}
): PreviewSource => {
  const newsletterId = useNewsletterId();
  const xsrfHeaders = useXsrfHeaders();

  const enabled = computed(() => toValue(options.enabled) ?? true);

  const debouncedNewsletter = debouncedRef(
    computed<Partial<Newsletter>>((prev) => {
      const next = toValue(input).newsletter;
      if (prev !== undefined && isDeepEqual(prev, next)) {
        return prev;
      }
      return next;
    }),
    500,
    { enabled }
  );

  const query = useQuery({
    queryKey: [newsletterId, "subscribe-page-preview", debouncedNewsletter],
    enabled,
    gcTime: 10_000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn({ signal }) {
      return fetchSubscribeTemplate(
        { newsletter: debouncedNewsletter.value },
        { signal, headers: xsrfHeaders.value }
      );
    },
  });

  const isStableFetching = useStablePending(query.isFetching);

  return {
    html: query.data,
    error: query.error,
    pending: computed(() => query.isLoading.value || isStableFetching.value),
  };
};

export const subscribePagePreviewSource = (
  input: MaybeRefOrGetter<SubscribePagePreviewInput>
): PreviewSourceFactory<"subscribe_page_preview:subscribe"> => ({
  key: "subscribe_page_preview:subscribe",
  create: ({ enabled }) => {
    return useSubscribePagePreview(input, { enabled });
  },
});
